import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline'
import { Overview } from 'components/Overview'
import { removeNonPagePermissions } from 'hooks/hasPagePermissions'
import { usePermissions } from 'hooks/usePermissions'
import Mers from 'pages/Loan/MERS'
import Notes from 'pages/Loan/Servicing/Notes/Notes'
import { loanOverviewAndStepLogic } from 'pages/LoanOverview/loanOverviewAndStepLogic'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { isVendorEnabled } from 'services'
import Api from 'services/api'
import { setLoanNumber } from 'utils/setLoanNumber'

import { ACH } from './ACH'
import { Contacts } from './Contacts'
import { DefaultTracking } from './DefaultTracking'
import { EscrowLedger } from './EscrowLedger'
import { Fci } from './Fci'
import { Investors } from './Investors'
import { Invoice } from './Invoice'
import { LoanCharges } from './LoanCharges'
import { LoanExtension } from './LoanExtension'
import { LoanPayments } from './LoanPayments'
import { NotePageType } from './Notes/types'
import { ServicingParticipation } from './Participation'
import { Payoff } from './Payoff'
import { Penalties } from './Penalties'
import { ServicingDocument } from './ServicingDocument'
import { ServicingNote } from './ServicingNotes/ServicingNote'
import { TrustLedger } from './TrustLedger'

export default function Servicing() {
  const [selectedMenu, setSelectedMenu] = useState('')
  const [menuItems, setMenuItems] = useState<any>({})
  const [isFciEnabled, setIsFciEnabled] = useState(false)

  const location = useLocation()
  const navigate = useHistory()

  useEffect(() => {
    isVendorEnabled('Fci').then(setIsFciEnabled)
  }, [])

  useEffect(() => {
    const menu = location.search.replace('?menu=', '')
    if (menu.length) setSelectedMenu(menu)
  }, [location])

  const setMenu = (menuItem: string) => {
    const loanNumber = Api.getLoanNumber()
    navigate.push(`/servicing/${loanNumber}?menu=${menuItem}`)
  }

  useEffect(() => {
    setLoanNumber()
    const loanNumber = Api.getLoanNumber()
    loanOverviewAndStepLogic(loanNumber, '')
  }, [])

  const { auth } = useSelector((state: any) => {
    return {
      auth: state.auth,
    }
  })

  const { hasPermission } = usePermissions()

  useEffect(() => {
    // if (Object.keys(menuItems).length) return
    let temp: any = {}
    if (hasPermission('MANAGE_SERVICING_NOTES')) temp['servicingNotes'] = 'Servicing Information'
    temp['ach'] = 'ACH'
    if (hasPermission('MANAGE_TRUST_LEDGER')) temp['trustLedger'] = 'Trust Ledger'
    if (hasPermission('MANAGE_SERVICING_DOCS')) {
      temp['servicingDocument'] = 'Servicing Documents'
    }
    if (hasPermission('MANAGE_SERVICING_NOTES')) temp['loanExtensions'] = 'Loan Extensions'
    if (hasPermission('MANAGE_SERVICING_PARTICIPATION')) temp['participation'] = 'Participation'

    if (hasPermission('ADMIN_TO_AE_PROFILE_PERMISSION')) temp['defaultTracking'] = 'Default Tracking'

    if (hasPermission('ADMIN_TO_AE_PROFILE_PERMISSION')) temp['notesForServicing'] = 'Servicing Notes'
    if (hasPermission('ADMIN_TO_AE_PROFILE_PERMISSION')) temp['globalNotes'] = 'Notes'
    if (hasPermission('MANAGE_TASKS')) temp['tasks'] = 'Tasks'
    if (Object.keys(temp).length) temp['invoice'] = 'Invoices'
    temp['orderValuation'] = 'Order Valuation'
    temp['payoff'] = 'Payoff'
    temp['budget'] = 'Budget & Scope of Work'
    temp['loanPayments'] = 'Loan Payments'
    temp['loanCharges'] = 'Loan Charges'
    temp['penalties'] = 'Penalties'
    temp['contacts'] = 'Contacts'
    if (isFciEnabled) temp['fci'] = 'FCI'
    temp['escrowLedger'] = 'Escrow Ledger'
    temp['investors'] = 'Investors'
    temp['mers'] = 'MERS #'

    removeNonPagePermissions(temp, ['pipeline', 'servicing'])
    setMenuItems(temp)
    if (Object.keys(temp).length && selectedMenu.length === 0) setMenu(Object.keys(temp)[0])
  }, [auth, isFciEnabled])

  const renderFragment = useMemo(() => {
    switch (selectedMenu) {
      case 'trustLedger':
        return <TrustLedger />
      case 'servicingDocument':
        return <ServicingDocument />
      case 'notesForServicing':
        return <Notes child={true} notePageType={NotePageType.SERVICING} />
      case 'globalNotes':
        return <Notes child={true} />
      case 'loanExtensions':
        return <LoanExtension />
      case 'servicingNotes':
        return <ServicingNote />
      case 'participation':
        return <ServicingParticipation />
      case 'defaultTracking':
        return <DefaultTracking />
      case 'invoice':
        return <Invoice />
      case 'payoff':
        return <Payoff />
      case 'loanPayments':
        return <LoanPayments />
      case 'loanCharges':
        return <LoanCharges />
      case 'fci':
        return <Fci />
      case 'contacts':
        return <Contacts />
      case 'ach':
        return <ACH />
      case 'escrowLedger':
        return <EscrowLedger />
      case 'penalties':
        return <Penalties />
      case 'investors':
        return <Investors />
      case 'mers':
        return <Mers />
      default: {
        return <div></div>
      }
    }
  }, [selectedMenu])

  const renderMenu = (menus: Record<string, string>) => {
    return (
      <ul className="sidebar-items flex flex-col p-4 pb-10">
        {Object.keys(menus).map((item: string, index) => {
          const isActive = selectedMenu === item
          if (item === 'tasks') {
            return (
              <li key={index} className="py-2 hover:underline cursor-pointer">
                <Link to={`/tasks/loan/${Api.getLoanNumber()}`} target="_blank">
                  <span className="flex gap-2 items-center">
                    <span>
                      {index + 1}. {menus[item]}
                    </span>
                    <ArrowTopRightOnSquareIcon className="w-4 h-4"></ArrowTopRightOnSquareIcon>
                  </span>
                </Link>
              </li>
            )
          }
          if (item === 'orderValuation') {
            return (
              <li key={index} className="py-2 hover:underline cursor-pointer">
                <Link to={`/order_valuation/${Api.getLoanNumber()}`} target="_blank">
                  <span className="flex gap-2 items-center">
                    <span>
                      {index + 1}. {menus[item]}
                    </span>
                    <ArrowTopRightOnSquareIcon className="w-4 h-4"></ArrowTopRightOnSquareIcon>
                  </span>
                </Link>
              </li>
            )
          }
          if (item === 'budget') {
            return (
              <li key={index} className="py-2 hover:underline cursor-pointer">
                <Link to={`/budget/${Api.getLoanNumber()}`} target="_blank">
                  <span className="flex gap-2 items-center">
                    <span>
                      {index + 1}. {menus[item]}
                    </span>
                    <ArrowTopRightOnSquareIcon className="w-4 h-4"></ArrowTopRightOnSquareIcon>
                  </span>
                </Link>
              </li>
            )
          }
          return (
            <li
              key={index}
              onClick={() => {
                setMenu(item)
              }}
              className="py-2"
            >
              <p className={`hover:underline cursor-pointer ${isActive ? 'border px-4 py-1 bg-zinc-100' : 'py-1'}`}>
                {index + 1}. {menus[item]}
              </p>
            </li>
          )
        })}
      </ul>
    )
  }

  return (
    <div className="py-6 px-2">
      <Overview title="Servicing" />
      <div className="max-w-screen-2xl m-auto grid grid-cols-12 md:gap-6">
        <div className="sidebar-left col-span-12 md:col-span-3 shrink-0 bg-white shadow1 rounded mb-4">
          {renderMenu(menuItems)}
        </div>
        <div className="relative h-fit content-right col-span-12 md:col-span-9 bg-white p-4 rounded shadow1 mb-4">
          <h2 className="text-2xl font-bold flex items-center mb-5">{menuItems[selectedMenu]}</h2>
          {renderFragment}
        </div>
      </div>
    </div>
  )
}
